const READY = 'ready';
const PLANNED = 'planned';

const ready = (key, label, category, meshKey, aliases) => ({
  key,
  label,
  category,
  status: READY,
  meshKey,
  picker: true,
  operational: true,
  aliases,
});

const planned = (key, label, category, aliases, operational = true) => ({
  key,
  label,
  category,
  status: PLANNED,
  picker: false,
  operational,
  aliases,
});

const catalogued = (key, label, category, aliases) => ready(
  key,
  label,
  category,
  key,
  aliases,
);

const RENDER_TYPES = [
  ready('car', 'Pickup / Utility', 'site', 'car', [
    'pickup', 'utility vehicle', 'utility', 'crew cab', 'site inspection vehicle',
    'security vehicle', 'suv', 'jeep', 'car', 'sedan', 'crossover', 'hatchback', 'van',
  ]),
  ready('truck', 'Tipper / Dumper', 'haulage', 'truck', [
    'tractor tipper', 'multi axle tipper', 'tipper truck', 'dump truck', 'rock dumper',
    'tipper', 'dumper', 'lorry', 'cargo', 'truck',
  ]),
  ready('bus', 'Staff Bus', 'site', 'bus', [
    'worker transport bus', 'staff bus', 'mini bus', 'minibus', 'ambulance', 'coach', 'bus',
  ]),
  ready('jcb', 'Backhoe Loader / JCB', 'excavation', 'jcb', [
    'backhoe loader', 'backhoe', 'jcb',
  ]),
  ready('trailer', 'Trailer', 'haulage', 'trailer', [
    'semi trailer', 'semi-trailer', 'tractor trailer', 'tractor trolley', 'trailer',
  ]),
  ready('mixer', 'Transit Mixer', 'haulage', 'mixer', [
    'transit mixer', 'concrete mixer', 'rmc', 'mixer', 'tm',
  ]),

  catalogued('wheel_loader', 'Wheel Loader / Pay Loader', 'excavation', [
    'front end loader', 'wheel loader', 'pay loader', 'payloader', 'loader',
  ]),
  catalogued('rigid_haul_truck', 'Rigid Haul Truck', 'haulage', [
    'mining dump truck', 'rigid dump truck', 'haul truck', 'mining dumper', 'off highway truck',
  ]),
  catalogued('adt', 'Articulated Dump Truck', 'haulage', [
    'articulated dump truck', 'articulated dumper', 'adt',
  ]),
  catalogued('water_tanker', 'Water Tanker', 'support', [
    'tractor mounted water tanker', 'dust suppression truck', 'mist cannon vehicle',
    'water sprinkler', 'water bowser', 'water tanker',
  ]),
  catalogued('fuel_bowser', 'Fuel Bowser', 'support', [
    'mobile fuel tanker', 'fuel delivery truck', 'lube service truck', 'lubrication truck',
    'lubrication vehicle', 'diesel bowser', 'diesel tanker', 'fuel bowser', 'fuel tanker',
  ]),
  catalogued('service_vehicle', 'Service Vehicle', 'support', [
    'tyre service truck', 'mobile workshop', 'workshop truck', 'maintenance van',
    'breakdown vehicle', 'recovery vehicle', 'welding vehicle', 'service truck',
    'service van', 'service vehicle', 'tow truck',
  ]),
  catalogued('motor_grader', 'Motor Grader', 'earthmoving', ['motor grader', 'grader']),
  catalogued('forklift', 'Forklift', 'lifting', ['fork lift', 'forklift']),
  catalogued('telehandler', 'Telehandler', 'lifting', ['telehandler', 'telescopic handler']),
  catalogued('tractor', 'Tractor', 'site', ['tractor mounted compressor', 'tractor']),
  catalogued('underground_lhd', 'Underground LHD', 'underground', [
    'load haul dump', 'underground loader', 'underground lhd', 'lhd',
  ]),
  catalogued('underground_dumper', 'Underground Dumper', 'underground', [
    'underground dump truck', 'underground dumper', 'mine dump truck',
  ]),

  catalogued('wheeled_excavator', 'Wheeled Excavator', 'excavation', ['wheeled excavator']),
  catalogued('wheel_dozer', 'Wheel Dozer', 'earthmoving', ['wheel dozer']),
  catalogued('wheeled_drill_rig', 'Wheeled Drill Rig', 'drilling', [
    'wheeled drill rig', 'truck mounted drill',
  ]),
  catalogued('wheeled_crane', 'Wheeled Crane', 'lifting', [
    'truck mounted crane', 'pick and carry crane', 'rough terrain crane',
    'telescopic crane', 'hydraulic crane', 'mobile crane', 'hydra crane', 'hydra',
  ]),
  catalogued('pneumatic_tyre_roller', 'Pneumatic Tyre Roller', 'earthmoving', [
    'pneumatic tyre roller', 'pneumatic roller',
  ]),
  planned('excavator', 'Excavator (ambiguous family)', 'excavation', [
    'long reach excavator', 'hydraulic excavator', 'mini excavator', 'hydraulic shovel',
    'mining shovel', 'rope shovel', 'excavator',
  ], false),
  planned('bulldozer', 'Bulldozer (ambiguous family)', 'earthmoving', [
    'crawler dozer', 'bulldozer', 'dozer',
  ], false),
  planned('drill_rig', 'Drill Rig (ambiguous family)', 'drilling', [
    'rotary blast hole drill', 'blast hole drill', 'exploration drill rig',
    'underground drill jumbo', 'core drilling machine', 'down the hole drill',
    'dth drill', 'crawler drill', 'wagon drill', 'jumbo drill', 'rotary drill',
    'hydraulic drill', 'drill rig',
  ], false),
  planned('road_roller', 'Road Roller (ambiguous family)', 'earthmoving', [
    'vibratory compactor', 'vibratory roller', 'soil compactor', 'road roller',
    'compactor', 'roller',
  ], false),

  // Explicitly excluded from the tyre-managed catalog, retained only so setup
  // imports can explain why a source record is not eligible.
  planned('rock_breaker', 'Rock Breaker Attachment', 'breaking', [
    'breaker excavator', 'hydraulic breaker', 'hydraulic hammer', 'rock breaker',
    'rock hammer', 'rock splitter',
  ], false),
  planned('mobile_crusher', 'Mobile Crusher (excluded)', 'processing', [
    'mobile crushing plant', 'track mounted crusher', 'mobile jaw crusher',
    'mobile cone crusher', 'mobile impact crusher', 'mobile crusher',
  ], false),
  planned('mobile_screen', 'Mobile Screening Plant (excluded)', 'processing', [
    'mobile screening plant', 'vibrating screen', 'scalping screen', 'trommel screen',
    'grizzly screen', 'mobile screen', 'screening plant',
  ], false),
  planned('surface_miner', 'Surface Miner (excluded)', 'mining', [
    'continuous miner', 'surface miner', 'highwall miner', 'roadheader', 'dragline',
  ], false),
  planned('air_compressor', 'Air Compressor (review separately)', 'support', [
    'rock breaker compressor', 'portable air compressor', 'diesel air compressor',
    'crawler compressor', 'compressor truck', 'air compressor', 'compressor',
  ], false),
  planned('dg_set', 'DG Set (review separately)', 'power', [
    'diesel generator', 'generator trailer', 'portable generator', 'dg set', 'genset', 'generator',
  ], false),
  planned('lighting_tower', 'Lighting Tower (review separately)', 'power', [
    'mobile lighting tower', 'lighting tower', 'light tower',
  ], false),
];

const MESH_KEYS = RENDER_TYPES
  .filter((entry) => entry.status === READY)
  .map((entry) => entry.meshKey);

const MESH_LABELS = {
  car: 'Passenger car',
  truck: 'Commercial truck',
  bus: 'Passenger bus',
  jcb: 'Backhoe loader',
  trailer: 'Semi-trailer',
  mixer: 'Transit mixer',
};

const BY_KEY = new Map(RENDER_TYPES.map((entry) => [entry.key, entry]));

const tokenise = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()
  .split(' ')
  .filter(Boolean);

const ALIAS_ENTRIES = RENDER_TYPES.flatMap((entry) => entry.aliases.map((alias) => ({
  key: entry.key,
  alias,
  tokens: tokenise(alias),
}))).sort((a, b) => b.tokens.length - a.tokens.length || b.alias.length - a.alias.length);

const containsTokens = (haystack, needle) => (
  needle.length > 0
  && haystack.some((_, start) => needle.every((token, offset) => haystack[start + offset] === token))
);

const findAliasHit = (vehicleType) => {
  const tokens = tokenise(vehicleType);
  if (!tokens.length) return null;
  return ALIAS_ENTRIES.find((entry) => containsTokens(tokens, entry.tokens)) || null;
};

const renderTypeOf = (key) => BY_KEY.get(key) || null;

const resolveRenderType = (vehicleType, mappedKey) => {
  if (mappedKey && BY_KEY.has(mappedKey)) {
    const entry = BY_KEY.get(mappedKey);
    return {
      key: entry.key,
      label: entry.label,
      meshKey: entry.status === READY ? entry.meshKey : undefined,
      status: entry.status,
      operational: entry.operational,
      source: 'mapping',
    };
  }
  if (mappedKey && MESH_KEYS.includes(mappedKey)) {
    return {
      key: mappedKey,
      label: MESH_LABELS[mappedKey],
      meshKey: mappedKey,
      status: READY,
      operational: true,
      source: 'mapping',
    };
  }

  const hit = findAliasHit(vehicleType);
  if (!hit) {
    return {
      key: null,
      label: null,
      meshKey: undefined,
      status: null,
      operational: false,
      source: 'fallback',
    };
  }

  const entry = BY_KEY.get(hit.key);
  return {
    key: entry.key,
    label: entry.label,
    meshKey: entry.status === READY ? entry.meshKey : undefined,
    status: entry.status,
    operational: entry.operational,
    source: 'alias',
    matchedAlias: hit.alias,
  };
};

const resolveMeshKey = (vehicleType, mappedKey) => resolveRenderType(vehicleType, mappedKey).meshKey;

const PICKER_VEHICLE_TYPES = RENDER_TYPES
  .filter((entry) => entry.picker && entry.operational)
  .map(({ key, label, category, status, meshKey }) => ({
    key, label, category, status, meshKey,
  }));

const SILHOUETTE_ALIASES = MESH_KEYS.reduce((acc, meshKey) => {
  const phrases = new Set();
  RENDER_TYPES.forEach((entry) => {
    if (entry.status !== READY || entry.meshKey !== meshKey) return;
    if (entry.key === meshKey) phrases.add(meshKey);
    entry.aliases.forEach((alias) => phrases.add(alias));
  });
  acc[meshKey] = [...phrases];
  return acc;
}, {});

export {
  MESH_KEYS,
  MESH_LABELS,
  PICKER_VEHICLE_TYPES,
  READY,
  PLANNED,
  RENDER_TYPES,
  SILHOUETTE_ALIASES,
  renderTypeOf,
  resolveMeshKey,
  resolveRenderType,
  tokenise,
};
